import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Eye, Instagram } from "lucide-react";

const GallerySection = () => {
  const [activeFilter, setActiveFilter] = useState("tous");

  const categories = [
    { id: "tous", name: "Toutes nos créations", count: 8 },
    { id: "layer-cakes", name: "Layer Cakes", count: 3 },
    { id: "cupcakes", name: "Cupcakes", count: 2 },
    { id: "number-cakes", name: "Number Cakes", count: 1 },
    { id: "verrines", name: "Verrines", count: 1 },
    { id: "bento", name: "Bento Cakes", count: 1 }
  ];

  const creations = [
    {
      id: 1,
      title: "Gâteau de mariage aux fleurs blanches",
      category: "layer-cakes",
      image: "/images/wedding1.jpg",
      description: "Layer cake élégant décoré de roses blanches et perles dorées"
    },
    {
      id: 2,
      title: "Cupcakes dorés festifs",
      category: "cupcakes",
      image: "/images/cupcakes1.jpg",
      description: "Cupcakes vanille avec glaçage crémeux et perles dorées"
    },
    {
      id: 3,
      title: "Assortiment de cupcakes",
      category: "cupcakes",
      image: "/images/cupcakes2.jpg",
      description: "Boîte de cupcakes assortis avec décoration dorée"
    },
    {
      id: 4,
      title: "Layer cake chocolat",
      category: "layer-cakes",
      image: "/images/layercake1.jpg",
      description: "Gâteau au chocolat avec glaçage lisse et décoration moderne"
    },
    {
      id: 5,
      title: "Création pâtissière spéciale",
      category: "layer-cakes",
      image: "/images/creation2.jpg",
      description: "Gâteau personnalisé avec décoration artistique"
    },
    {
      id: 6,
      title: "Gâteau élégant rose",
      category: "layer-cakes",
      image: "/images/layercake2.jpg",
      description: "Layer cake aux tons roses avec finition professionnelle"
    },
    {
      id: 7,
      title: "Bento cake individuel",
      category: "bento",
      image: "/images/bento1.jpg",
      description: "Mini gâteau dans sa boîte, parfait pour offrir"
    },
    {
      id: 8,
      title: "Création sur mesure",
      category: "layer-cakes",
      image: "/images/creation1.jpg",
      description: "Gâteau personnalisé selon les goûts du client"
    }
  ];

  const filteredCreations = activeFilter === "tous" 
    ? creations 
    : creations.filter(creation => creation.category === activeFilter);

  return (
    <section id="creations" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Nos créations
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Découvrez nos plus belles réalisations : gâteaux d'anniversaire, de mariage et bien plus encore. 
            Chaque photo représente une création faite main, unique et personnalisée selon vos envies.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={activeFilter === category.id ? "default" : "outline"}
              onClick={() => setActiveFilter(category.id)}
              className={`transition-smooth ${
                activeFilter === category.id
                  ? "gradient-primary text-white shadow-elegant"
                  : "border-primary text-primary hover:bg-primary hover:text-white"
              }`}
            >
              {category.name}
              <Badge 
                variant="secondary" 
                className="ml-2 bg-white/20 text-current border-0"
              >
                {category.count}
              </Badge>
            </Button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredCreations.map((creation) => (
            <Dialog key={creation.id}>
              <DialogTrigger asChild>
                <div className="group cursor-pointer">
                  <div className="relative overflow-hidden rounded-xl shadow-md hover:shadow-elegant transition-smooth">
                    <img
                      src={creation.image}
                      alt={creation.title}
                      className="w-full h-64 object-cover group-hover:scale-105 transition-smooth"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-smooth flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-smooth">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-3">
                          <Eye className="w-6 h-6 text-primary" />
                        </div>
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                      <h3 className="text-white font-semibold text-sm">
                        {creation.title}
                      </h3>
                    </div>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <img
                      src={creation.image}
                      alt={creation.title}
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                  <div className="flex flex-col justify-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {creation.title}
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {creation.description}
                    </p>
                    <div className="space-y-3">
                      <Button
                        className="w-full gradient-primary text-white shadow-elegant"
                        onClick={() => {
                          const element = document.querySelector("#contact");
                          if (element) {
                            element.scrollIntoView({ behavior: "smooth" });
                          }
                        }}
                      >
                        Commander un gâteau similaire
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full border-primary text-primary hover:bg-primary hover:text-white"
                        onClick={() => window.open("https://wa.me/33753797698", "_blank")}
                      >
                        Demander un devis WhatsApp
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          ))}
        </div>

        {/* Instagram CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-pink-50 to-yellow-50 rounded-2xl p-8 border border-pink-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              📸 Encore plus de créations sur Instagram
            </h3>
            <p className="text-gray-600 mb-6">
              Suivez @sweetsby_gem pour découvrir nos dernières réalisations, 
              les coulisses de l'atelier et nos nouveautés !
            </p>
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.open("https://www.instagram.com/sweetsby_gem", "_blank")}
              className="border-2 border-accent text-accent hover:bg-accent hover:text-white transition-smooth"
            >
              <Instagram className="w-5 h-5 mr-2" />
              Voir sur Instagram
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;